import "@esenda/core-ui/core-ui.css";
import { createApp } from "vue";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";
import "./assets/style/index.css";
import router from "./plugins/router";
import i18n from "./plugins/vi18n";
import { createAppVuetify } from "./plugins/vuetify";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { useAlert } from "@esenda/core-ui/composables";
import {
    parseCheckoutTheme,
    warnInvalidCheckoutTheme,
} from "@/theme/checkoutTheme";
import {
    resolveCheckoutTheme,
    mountCheckoutThemeStyles,
    CheckoutThemeKey,
} from "@/theme/applyCheckoutTheme";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App);

const { showAlert } = useAlert();

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: `${import.meta.env.VITE_APP_NAME}@${
        import.meta.env.VITE_APP_VERSION
    }`,
    tracePropagationTargets: [import.meta.env.VITE_API_URL, /^\//],
    integrations: [
        Sentry.browserTracingIntegration({
            router,
            routeLabel: "name",
        }),
        Sentry.replayIntegration({
            // masks PI from inputs/text on session replays
            // can be altered to allow only certain inputs to be hidden
            // more on https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/privacy/
            maskAllText: true,
            // items in the dom with the following class will show up in the session replay
            unmask: [".sentry-unmask, [data-sentry-unmask]"],
            // Capture request information on in the network tab
            networkDetailAllowUrls: [],
            networkDetailDenyUrls: [],
            networkCaptureBodies: true,
        }),
        Sentry.vueIntegration({
            tracingOptions: {
                trackComponents: false, // this needs to be off until this issue is fixed https://github.com/getsentry/sentry-javascript/issues/8143
            },
        }),
    ],
    debug: import.meta.env.VITE_ENVIRONMENT !== "production",
    tracesSampler: ({ name }) => {
        // Do not sample health checks ever
        if (
            name.includes("OpayoIframe") ||
            name.includes("LloydsChallengeIframe") ||
            name.includes("LloydsMethodFormIframe")
        ) {
            // Drop this completelty, by setting its sample rate to 0%
            return 1;
        }

        // Else, use default sample rate
        return 0;
    },
    attachProps: true,
    attachStacktrace: true,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_ENVIRONMENT,
    enabled: import.meta.env.VITE_ENVIRONMENT !== "local",
    /** List taken from Sentry's gist https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry */
    ignoreErrors: [
        "Non-Error promise rejection captured",
        // Random plugins/extensions
        "top.GLOBALS",
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "http://tt.epicplay.com",
        "Can't find variable: ZiteReader",
        "jigsaw is not defined",
        "ComboSearch is not defined",
        "http://loading.retry.widdit.com/",
        "atomicFindClose",
        // Facebook borked
        "fb_xd_fragment",
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        "bmi_SafeAddOnload",
        "EBCallBackMessageReceived",
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        "conduitPage",
        // Network errors such as going offline or being blocked by a proxy
        "Failed to fetch",
        /**
         * error triggered by IP addresses from Microsoft Corporation. This seems to be the issue:
         * "antifingerprint not defined yet. will try and handle event after its ready."
         * No solution/cause yet, but threads recommend to filter out the error:
         * https://github.com/getsentry/sentry-javascript/issues/9143
         * 688 events triggered in Sentry for 688 distinct users. The ip addresses point to Microsoft Corporation
         * */
        "TypeError: Illegal invocation",
        "TypeError: cancelled", // See https://stackoverflow.com/a/70452078
        // Sentry error triggered when RUM cannot be collected
        "TypeError ?(rum)",
        "(rum)",
        /^performance\.getEntriesByType is not a function\. \(In 'performance\.getEntriesByType\("navigation"\)', 'performance\.getEntriesByType' is undefined\)$/,
        /^s\.getEntriesByType is not a function\. \(In 's\.getEntriesByType\(r\)', 's\.getEntriesByType' is undefined\)$/,

        // Snapchat in-app browser errors
        /SCDynimacBridge/i,

        // browser extension errors
        /safariextension/i,
        /Honey\.safariextension/i,
    ],
    denyUrls: [
        // Safari webkit
        /.*webkit-masked-url.*/,
        /^webkit-masked-url:\/\/hidden\/$/,
        // Legacy Safari App Extensions (e.g. .../Honey.safariextension/h0.js)
        /\.safariextension\//i,
        // Newer Safari Web Extensions, if you see these too
        /^safari-web-extension:\/\//i,
        /^safari-extension:\/\//i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Other plugins
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        // Firefox extensions
        /^resource:\/\//i,
    ],
    // maximum number of characters a message can have
    // before it will be truncated
    maxValueLength: 1000,
});

// Tag all Sentry events with the embedding page's origin when loaded inside an iframe.
// window.self !== window.top throws a SecurityError in cross-origin iframes, which itself
// confirms embedding — so the catch branch correctly returns true.
const isEmbedded = (() => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
})();

if (isEmbedded) {
    // ancestorOrigins is supported in Chrome and Safari; document.referrer is the Firefox fallback.
    // Wrap URL parsing in try/catch because document.referrer may not be a valid absolute URL.
    const parentOrigin =
        window.location.ancestorOrigins?.[0] ??
        (() => {
            try {
                return document.referrer
                    ? new URL(document.referrer).origin
                    : null;
            } catch {
                return null;
            }
        })();
    if (parentOrigin) {
        Sentry.setTag("parent_origin", parentOrigin);
    }
}

/**
 * Checkout theme: read once from this iframe's own URL, before mount.
 * Held in memory only (never persisted), so internal navigation can't drop it
 * and each iframe on a partner page resolves its own theme independently.
 */
const checkoutThemeResult = parseCheckoutTheme(window.location.search);
warnInvalidCheckoutTheme(checkoutThemeResult);
Sentry.setTag("checkout_theme", checkoutThemeResult.status);
if (checkoutThemeResult.status === "invalid") {
    Sentry.setTag("checkout_theme_rejection", checkoutThemeResult.reason);
}

const checkoutTheme = resolveCheckoutTheme(checkoutThemeResult);
mountCheckoutThemeStyles(checkoutTheme);

const vuetify = createAppVuetify(checkoutTheme?.vuetifyColors);
app.provide(CheckoutThemeKey, checkoutTheme);

app.use(pinia).use(router).use(vuetify).use(i18n).mount("#app");

/** Ensure errors import module chunks failures are handled */
window.addEventListener("vite:preloadError", (event) => {
    // Added breadcrumb in Sentry so we can connect errors to this event
    Sentry.addBreadcrumb({
        category: "vite:preloadError",
        message: "Import module error triggered " + event,
        level: "info",
    });
    showAlert("alerts.newAppVersion");
});
